import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Bug, Image, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { formatDateString } from '@/lib/utils/format';
import {
  bugReportsService,
  type BugReportListItem,
  type BugReportStatus,
  type BugReportType,
} from '../services/bug-reports-service';
import { BugReportDetailDialog } from '../components/bug-report-detail-dialog';

// ── helpers ────────────────────────────────────────────────────────

const TYPE_EMOJI: Record<BugReportType, string> = {
  BUG: '🐛',
  CRASH: '💥',
  UI: '🎨',
  DATA: '📊',
  SUGGESTION: '💡',
};

const TYPE_LABEL: Record<BugReportType, string> = {
  BUG: 'Bug',
  CRASH: 'Crash',
  UI: 'Visual',
  DATA: 'Datos',
  SUGGESTION: 'Sugerencia',
};

const STATUS_LABEL: Record<BugReportStatus, string> = {
  NEW: 'Nuevo',
  IN_PROGRESS: 'En progreso',
  RESOLVED: 'Resuelto',
  WONT_FIX: 'No se va a arreglar',
};

const STATUS_VARIANT: Record<BugReportStatus, 'info' | 'warning' | 'success' | 'secondary'> = {
  NEW: 'info',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  WONT_FIX: 'secondary',
};

const PAGE_SIZE = 25;

// ── Component ──────────────────────────────────────────────────────

export function AdminBugReportsPage() {
  const [items, setItems] = useState<BugReportListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BugReportStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<BugReportType | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bugReportsService.list({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr?.response?.status === 403) {
        setError('Sin permisos para ver los bug reports.');
      } else {
        setError('Error al cargar los reportes. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // Reset page when filters change
  const handleStatusFilter = (v: string) => {
    setStatusFilter(v as BugReportStatus | 'all');
    setPage(1);
  };
  const handleTypeFilter = (v: string) => {
    setTypeFilter(v as BugReportType | 'all');
    setPage(1);
  };

  // Update the item in the list after status change from dialog
  const handleStatusChange = (updated: BugReportListItem) => {
    setItems((prev) =>
      prev.map((it) => (it.id === updated.id ? { ...it, status: updated.status } : it))
    );
  };

  return (
    <AppLayout variante="plataforma">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bug className="h-6 w-6" />
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Bug Reports</h1>
            {total > 0 && (
              <Badge variant="secondary">
                {total} {total === 1 ? 'reporte' : 'reportes'}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => void fetchList()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <Select value={statusFilter} onValueChange={handleStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="NEW">Nuevo</SelectItem>
              <SelectItem value="IN_PROGRESS">En progreso</SelectItem>
              <SelectItem value="RESOLVED">Resuelto</SelectItem>
              <SelectItem value="WONT_FIX">No se va a arreglar</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={handleTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="BUG">🐛 Bug</SelectItem>
              <SelectItem value="CRASH">💥 Crash</SelectItem>
              <SelectItem value="UI">🎨 Visual</SelectItem>
              <SelectItem value="DATA">📊 Datos</SelectItem>
              <SelectItem value="SUGGESTION">💡 Sugerencia</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void fetchList()}>
              Reintentar
            </Button>
          </div>
        )}

        {/* Table */}
        {!error && (
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">
                  {[...Array(8)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Fecha</TableHead>
                      <TableHead className="w-[100px]">Tipo</TableHead>
                      <TableHead className="w-[130px]">Estado</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead className="w-[80px] text-center">Imgs</TableHead>
                      <TableHead className="w-[70px] text-center">TG</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedId(item.id)}
                      >
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDateString(item.createdAt, 'dd MMM HH:mm')}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {TYPE_EMOJI[item.type]}{' '}
                            <span className="text-muted-foreground">{TYPE_LABEL[item.type]}</span>
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[item.status]}>
                            {STATUS_LABEL[item.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <button
                            className="text-sm font-medium text-left hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(item.id);
                            }}
                          >
                            {item.title}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.tenant?.name ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.user?.email ?? '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.imageCount > 0 ? (
                            <span className="flex items-center justify-center gap-1 text-sm">
                              <Image className="h-3.5 w-3.5" />
                              {item.imageCount}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {item.telegramSent ? (
                            <span className="text-green-500">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                          Sin reportes
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {!error && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground px-3">
              Página {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <BugReportDetailDialog
        reportId={selectedId}
        onClose={() => setSelectedId(null)}
        onStatusChange={handleStatusChange}
      />
    </AppLayout>
  );
}
